import React, { useContext, useEffect, useState } from "react";

import { useLocation } from "@reach/router";

import GlobalContext from "@/contexts/Global/GlobalContext";
import UserContext from "@/contexts/User/UserContext";
import useProfile from "@/hooks/useProfile";

import SessionAlertRenderer from "./components/SessionAlertRenderer";
import { useActiveSessionPolling } from "./hooks/useActiveSessionPolling";
import { useSessionStateManager } from "./hooks/useSessionStateManager";
import { shouldRunSessionPolling } from "./utils/sessionHelpers";

/**
 * SessionController - главный контроллер для управления сессиями
 *
 * Отвечает за:
 * 1. Периодический опрос сервера для получения активных сессий
 * 2. Управление состоянием активной сессии
 * 3. Отображение соответствующих алертов/уведомлений
 *
 * Поддерживает два типа сессий:
 * - VIDEO_CALL (видео звонки)
 * - CHAT (текстовые чаты)
 */
const SessionController: React.FC = () => {
  const { userID, isUserLoggedIn } = useContext(UserContext);
  const {
    currentSessionData,
    setCurrentSessionData,
    alertStatus,
    isAlertShown,
  } = useContext(GlobalContext);

  const location = useLocation();
  const { fetchProfile } = useProfile({ autoFetch: false });
  const [shouldPoll, setShouldPoll] = useState(false);

  useEffect(() => {
    if (isUserLoggedIn && userID === null) {
      fetchProfile();
    }
  }, [fetchProfile, isUserLoggedIn, userID]);

  // Определение необходимости запуска опроса сессий
  useEffect(() => {
    const shouldRun = shouldRunSessionPolling(location.pathname, isUserLoggedIn);
    setShouldPoll(shouldRun);
  }, [location.pathname, isUserLoggedIn]);

  // Запрос активных сессий
  const { data, loading, error } = useActiveSessionPolling({
    shouldPoll,
    isUserLoggedIn,
  });

  // Управление состоянием сессии
  useSessionStateManager({
    sessionData: data,
    loading,
    error,
    userID,
  });

  // Очистка состояния при размонтировании
  useEffect(
    () =>
      () => {
        setCurrentSessionData(null);
      },
    [setCurrentSessionData],
  );

  // Не показываем алерт если:
  // - Алерт скрыт
  // - Нет данных сессии и нет кастомного статуса алерта
  if (!isAlertShown || (!currentSessionData?.status && !alertStatus)) {
    return null;
  }

  return (
    <SessionAlertRenderer
      alertStatus={currentSessionData?.status || alertStatus}
      client={currentSessionData?.client}
      expert={currentSessionData?.expert}
      sessionId={currentSessionData?.id}
      sessionType={currentSessionData?.sessionType}
    />
  );
};

export default SessionController;
